/**
 * LiveSession — the co-driven-demo sync engine + chrome. Mounted by DemoLayout only
 * while a room is active (never in the static build). BOTH people can click anything:
 *   • joins the relay room — the HOST via `host_join` (carries the host token; the
 *     relay rejects hosting without it), the GUEST via plain `join` (denied until a
 *     live host has the room open, so the guest retries every few seconds — this also
 *     covers "guest opened the link before the host entered").
 *   • mirrors NAVIGATION both ways: any route change broadcasts `demo_nav {path, stamp}`
 *     and the other screen follows, unless it already moved somewhere newer (last click
 *     wins — sync-core). Reducer state mirrors in DemoStateProvider; in-page UI state
 *     in useSyncedState.
 *   • REPLAYS on late join: when a peer joins (`sync_request`), the host re-pushes its
 *     route, the demo data, and every shared UI value — all authoritative.
 *   • renders the status bar + the peer video.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSocket } from "../../shims/socket-client";
import { useDemoState } from "../DemoStateProvider";
import { useLiveRoom } from "./LiveRoom";
import { clearHostToken, getHostToken, type LiveRole } from "./live";
import { isStamp } from "./sync-core";
import { canBroadcast, navRegister, snapshotSyncedState } from "./useSyncedState";
import { LiveControlBar } from "./LiveControlBar";
import { LiveVideo } from "./LiveVideo";

/** Guest re-attempts a denied join this often (host may not have entered yet). */
const JOIN_RETRY_MS = 3000;
/** navRegister's single key: the shared route. */
const ROUTE = "route";

export default function LiveSession() {
  const { room, endRoom } = useLiveRoom();
  const navigate = useNavigate();
  const location = useLocation();
  const demo = useDemoState();

  const role: LiveRole = room?.role ?? "guest";
  const code = room?.code ?? "";

  const [peerConnected, setPeerConnected] = useState(false);

  // Full path INCLUDING the query string: sessions start via `/session?client=<id>`
  // and the client attachment lives in the query — mirroring pathname only would drop
  // it and show the other person an unattached session.
  const fullPath = location.pathname + location.search;

  // Latest values for use inside socket handlers without re-subscribing.
  const pathRef = useRef(fullPath);
  pathRef.current = fullPath;
  // demo state read through a ref so the host's re-sync snapshot is CURRENT, not the
  // seed captured when this effect first mounted (else every guest rejoin wipes the
  // demo back to seed and diverges).
  const demoRef = useRef(demo);
  demoRef.current = demo;
  // The route this screen is moving to BECAUSE the other person went there. Its arrival
  // must not be re-broadcast — that would bounce the navigation straight back.
  const followingTo = useRef<string | null>(null);

  // ── Join the room + wire relay handlers ─────────────────────────────────────
  useEffect(() => {
    if (!code) return;
    const socket = getSocket();
    const joinFrame = () =>
      role === "host"
        ? socket.emit("host_join", { sessionCode: code, hostToken: getHostToken() ?? "" })
        : socket.emit("join", { sessionCode: code });
    joinFrame();

    // Guest: a denied join means no live host yet (early guest / host reload) —
    // keep retrying quietly. Ignore denials for OTHER codes (the vendored BLS hooks
    // join their constant "demo-session" room on this same socket; its denial is
    // expected and harmless — frames flow through the live room instead).
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const onDenied = (payload?: { sessionCode?: string }) => {
      if (role === "host") return;
      if (payload?.sessionCode !== code) return;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(joinFrame, JOIN_RETRY_MS);
    };
    // Host token invalid/expired → drop the stale token, tear down, and bounce to the
    // /live sign-in so the host can re-authenticate.
    const onHostDenied = () => {
      clearHostToken();
      endRoom();
      navigate("/live");
    };

    // The host is the source of truth whenever someone (re)joins: push the route, the
    // demo data, and every shared UI value, all applied as authoritative by the joiner.
    // Also runs when the host itself (re)enters (host_ok), realigning a guest who kept
    // the room open through a host reload.
    const hostResync = () => {
      const s = getSocket();
      s.emit("demo_nav", { path: pathRef.current, stamp: navRegister.stampOf(ROUTE), force: true });
      const st = demoRef.current;
      s.emit("demo_action", { type: "__REPLACE__", state: { clients: st.clients, sessions: st.sessions, scripts: st.scripts } });
      s.emit("demo_ui_snapshot", { entries: snapshotSyncedState() });
    };
    const onHostOk = () => {
      if (role === "host") hostResync();
    };

    // The other person navigated. Follow — unless this screen already moved somewhere
    // NEWER: two navigations that cross on the wire end on the same page on both
    // screens (last click wins). A `force` (the host's resync) always wins.
    const onNav = (payload?: { path?: unknown; stamp?: unknown; force?: unknown }) => {
      setPeerConnected(true); // any inbound frame proves the peer is here
      if (typeof payload?.path !== "string") return;
      const stamp = isStamp(payload.stamp) ? payload.stamp : undefined;
      navRegister.applyRemote(ROUTE, payload.path, stamp, payload.force === true);
      if (navRegister.get(ROUTE) !== payload.path) return; // ours is newer — they follow us
      if (payload.path === pathRef.current) return; // already here
      followingTo.current = payload.path;
      navigate(payload.path);
    };
    const onSync = () => {
      // A peer (re)joined. Only the host re-pushes (it is the source of truth).
      setPeerConnected(true);
      if (role === "host") hostResync();
    };
    // The peer left the room (relay tells the survivor) → back to "waiting".
    const onPeerLeft = () => setPeerConnected(false);

    socket.on("join_denied", onDenied);
    socket.on("host_denied", onHostDenied);
    socket.on("host_ok", onHostOk);
    socket.on("demo_nav", onNav);
    socket.on("sync_request", onSync);
    socket.on("peer_left", onPeerLeft);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      socket.off("join_denied", onDenied);
      socket.off("host_denied", onHostDenied);
      socket.off("host_ok", onHostOk);
      socket.off("demo_nav", onNav);
      socket.off("sync_request", onSync);
      socket.off("peer_left", onPeerLeft);
      socket.emit("leave", { sessionCode: code });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, role]);

  // ── Mirror navigation: EITHER screen broadcasts its route changes ────────────
  useEffect(() => {
    if (!code) return;
    if (followingTo.current === fullPath) {
      followingTo.current = null; // we got here by following — don't bounce it back
      return;
    }
    followingTo.current = null;
    // Recorded even before a guest is synced (so the register mirrors this screen's
    // route); only broadcast once it may (a guest's landing page must not yank the host).
    const stamp = navRegister.setLocal(ROUTE, fullPath);
    if (stamp && canBroadcast()) getSocket().emit("demo_nav", { path: fullPath, stamp });
  }, [fullPath, code]);

  const end = useCallback(() => {
    getSocket().emit("leave", { sessionCode: code });
    endRoom();
    navigate("/");
  }, [code, endRoom, navigate]);

  if (!room) return null;

  return (
    <>
      <LiveControlBar role={role} peerConnected={peerConnected} code={code} onEnd={end} />
      {/* Waiting room: a guest who opened their booking-email link EARLY (or before
          the host entered) sees a calm holding state instead of a dead dashboard.
          Clears the moment anything arrives from the host. */}
      {role === "guest" && !peerConnected && (
        <div className="fixed inset-0 z-[36] flex flex-col items-center justify-center gap-4 bg-[var(--color-bg-deep)]/85 px-6 text-center backdrop-blur-sm">
          <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-[var(--color-gold)]" />
          <p className="font-heading text-2xl font-medium text-[var(--color-text-primary)]">
            You're in the right place.
          </p>
          <p className="max-w-sm text-[14px] leading-relaxed text-[var(--color-text-muted)]">
            Your guide will start the walkthrough shortly — this screen connects
            automatically the moment they join. Keep this tab open.
          </p>
        </div>
      )}
      <LiveVideo role={role} />
    </>
  );
}
